// Client-side role → capability mapping for nav gating and route guards.
//
// This mirrors the backend authority sets in Clms.Application.Security.Roles so the
// SPA offers exactly the actions the API would allow — hiding admin surfaces from a
// learner instead of letting them 403. The API remains the source of truth; these
// checks only shape the UI (a hand-typed URL is still bounced by RequireRole, and any
// call is still authorized server-side).

import { useSession } from "./session";
import { useTenantChoiceIfAny } from "./tenantPicker";

/** Canonical role codes — mirror of Roles.cs (Keycloak client roles). */
export const Role = {
  Learner: "OPPIJA",
  Manager: "ESIMIES",
  Hr: "HR",
  Admin: "ADMIN",
  ContentManager: "CONTENT_MANAGER",
  PlatformAdmin: "PLATFORM_ADMIN",
  ComplianceAuditor: "COMPLIANCE_AUDITOR",
  // Johdon raporttikäyttäjä (US-E9.1) — opens the leadership report view and nothing else. What it may
  // SEE there is not a capability question: the granted view scopes come from the server
  // (GET /api/view-scopes/me), never from this role, so there is no client-side mirror of the breadth.
  LeadershipReportViewer: "JOHDON_RAPORTTI",
  // Muutosjohtamisen vastuukonsultti (ADR-0016 §4.1) — Incedo's consultant, provisioned into each named
  // customer tenant, holding the whole consultant surface of the muutosjohtaminen module. An ordinary
  // tenant-scoped client role, which is what makes ADR-0016 §4.2's tenant narrowing need no new mechanism
  // here either: the roles in this session's token are the roles in THIS tenant.
  //
  // All three MJ codes ARE in the user directory's vocabulary (admin/roleVocabulary.ts, ADR-0016 §4.6), and
  // that is not the same list: this one says which surfaces MY session opens, that one says what SOMEBODY
  // ELSE's role is called. A code belongs there as soon as a holder can exist, and here only once it opens
  // something.
  ChangeLeadConsultant: "MJ_VASTUUKONSULTTI",
  // Muutosjohtamisen johdon raporttikäyttäjä (ADR-0016 §4.3) — the CUSTOMER's leadership reader, and a new
  // role rather than the existing `JOHDON_RAPORTTI` above: §4.3 is explicit that the two are different
  // authorities over different modules, and one code for both would make a kyselymoottori grant open a
  // change programme's published report.
  //
  // It is here as of MJ-US-E6.3, and the muutoskartta is what put it here: `change-map/published` is the
  // first surface in this SPA that a holder of this code may open. What they see there is the published
  // snapshot or the state `unpublished` — never the live map, which is a property of the ROUTE TABLE
  // (§7.7, §8.1) and not of this file.
  ChangeLeadershipReportViewer: "MJ_JOHDON_RAPORTTI",
  // Muutosjohtamisen toimenpiteen omistaja (ADR-0016 §4.1, §4.4) — the CUSTOMER's named action owner, and
  // the module's third code. It is here as of MJ-US-E7.2, which is the story that gave it something to
  // open: `/change-actions`, the two self-scoped routes of §4.5 property 3 (`/api/my/change-actions`).
  // Until then it was deliberately absent, because a code with no capability behind it reads as an
  // authority the SPA honours while opening nothing.
  //
  // MJ-D5 (`a763bae3`, 17.10.2026) is what makes a holder a signed-in person at all: before it, the column
  // `change_actions.owner_user_id` named somebody who might never log in. The code is still granted per
  // tenant like every other client role, and NOT through the consultant's Roolit tab — §4.7 decision 2
  // withholds it there by name.
  ChangeActionOwner: "MJ_TOIMENPITEEN_OMISTAJA",
  // HRM-henkilöstöasiantuntija (ADR-0020 §5) — the employment record's own authority, a `clms-api` client
  // role like every other code here. It is in this file as of ADR-0020 §16 task 5, which is the story that
  // gave it something to open: the employment-record list and page, and the two audited reveals behind
  // them. §16 task 1 put the code in both realm files; until this story it opened nothing in the SPA, and
  // a code with no capability behind it reads as an authority the SPA honours while opening nothing.
  //
  // The tenant ADMIN holds `employmentRecordView` BESIDE this role and does not hold
  // `hrmPersonnelRecord` — see those two entries, which is where §4's whole decision lives.
  HrmPersonnelExpert: "HRM_HENKILOSTOASIANTUNTIJA",
} as const;

// The realm (infra/keycloak/realm-clms.json) now ships exactly these canonical codes.
// The lowercase / hyphenated aliases below are kept only as a defensive normalization
// for any legacy token spelling — new tokens carry the canonical code directly.
const ALIASES: Record<string, string> = {
  learner: Role.Learner,
  oppija: Role.Learner,
  manager: Role.Manager,
  esimies: Role.Manager,
  hr: Role.Hr,
  admin: Role.Admin,
  "tenant-admin": Role.Admin,
  tenant_admin: Role.Admin,
  "content-manager": Role.ContentManager,
  content_manager: Role.ContentManager,
  "platform-admin": Role.PlatformAdmin,
  platform_admin: Role.PlatformAdmin,
  "compliance-auditor": Role.ComplianceAuditor,
  compliance_auditor: Role.ComplianceAuditor,
  auditor: Role.ComplianceAuditor,
  johdon_raportti: Role.LeadershipReportViewer,
  "johdon-raportti": Role.LeadershipReportViewer,
};

function normalize(role: string): string {
  const key = role.trim().toLowerCase();
  return ALIASES[key] ?? role.trim().toUpperCase();
}

/** A gated area of the app; "My trainings" needs none (every session sees it). */
export type Capability =
  | "contentManagement"
  | "trainingAssignment"
  | "userAdministration"
  | "auditRead"
  | "supportAccessVisibility"
  | "platformAdmin"
  | "complianceReporting"
  | "questionnaireManagement"
  | "questionnaireReportRead"
  | "viewScopeGrantManagement"
  | "certificateOperations"
  | "changeProgramManagement"
  | "changePublishedReportRead"
  | "changeOwnActions"
  | "employmentRecordView"
  | "hrmPersonnelRecord";

// Capability → the canonical roles that hold it. Mirrors the Roles.cs sets:
//   ContentManagement   = { ADMIN } (+ content-manager once the realm has it)
//   TrainingAssignment  = { ADMIN, HR }
//   user administration (US-1.5) is the tenant admin only
//   platform administration (US-1.6) is the cross-tenant operator only
const CAPABILITY_ROLES: Record<Capability, readonly string[]> = {
  contentManagement: [Role.Admin, Role.ContentManager],
  trainingAssignment: [Role.Admin, Role.Hr],
  userAdministration: [Role.Admin],
  // Audit read/verify (US-8.1) is a tenant-wide compliance view — auditor, HR, or tenant admin.
  // Mirrors AuditEndpoints' RequireAnyRole(Hr, Admin, ComplianceAuditor).
  auditRead: [Role.Admin, Role.Hr, Role.ComplianceAuditor],
  // ADR-0018 §8 — the tenant's own view of Verso's SUPPORT ACCESS into it. Mirrors `Roles.TenantWide`
  // { ADMIN, HR }, the tenant-pääkäyttäjä pair, which is the gate
  // `TenantSupportAccessEndpoints.ListAsync` applies.
  //
  // Deliberately NOT `auditRead`, which holds the same two plus COMPLIANCE_AUDITOR: that trio reads the
  // CHAIN, and this page is a statement about current state — does Verso have access to us right now —
  // that the pääkäyttäjä is the one to act on. And deliberately not `complianceReporting`, which adds
  // ESIMIES: a team-scoped authority has nothing to say about access to the whole tenant.
  supportAccessVisibility: [Role.Admin, Role.Hr],
  platformAdmin: [Role.PlatformAdmin],
  // Compliance coverage reporting (E7, US-7.1/7.2): HR/admin see the tenant-wide report, an esimies
  // sees their own team — mirrors Roles.ComplianceReporting { ADMIN, HR, ESIMIES }. Breadth is decided
  // server-side by the row-level access scope; this only surfaces the nav entry + route.
  complianceReporting: [Role.Admin, Role.Hr, Role.Manager],
  // Kyselymoottori questionnaire management (KM-E1 / US-E1.1) — the successor of the retired legacy
  // survey module's surveyManagement (ADR-0006). Mirrors Roles.QuestionnaireManagement { ADMIN, HR }.
  // Membership only opens the management surface; tenant isolation stays server-side (F-4 row filter).
  questionnaireManagement: [Role.Admin, Role.Hr],
  // Kyselymoottori report READ (KM-E7 / US-E7.1, US-E9.1) — who may open a report surface at all. Mirrors
  // Roles.QuestionnaireReportRead { ADMIN, HR, JOHDON_RAPORTTI }. It decides only that the leadership view
  // may be opened, never HOW WIDE: the granted view scopes come from the server (GET /api/view-scopes/me),
  // which is what keeps "may open" and "how much" from collapsing into one client-side check.
  questionnaireReportRead: [Role.Admin, Role.Hr, Role.LeadershipReportViewer],
  // Kyselymoottori view-scope GRANTING (KM-E7 / US-E7.1 vaihe 1, FR-Q.48) — who may grant and revoke a
  // leadership viewer's scopes. Mirrors Roles.ViewScopeGrantManagement { ADMIN, HR }: the
  // tenant-pääkäyttäjä, which the spec calls the HR-asiantuntija.
  //
  // It holds the SAME two roles as `questionnaireManagement` today and is still its own capability,
  // because the backend set it mirrors is its own set: `Roles.ViewScopeGrantManagement` deliberately
  // excludes JOHDON_RAPORTTI (granting is done TO a leadership viewer, never BY one — FR-Q.50), and if
  // either set is widened later it must be possible to follow it here without dragging the other along.
  viewScopeGrantManagement: [Role.Admin, Role.Hr],
  // Certificate OPERATIONS (E6 / US-6.1): seeing the tenant's failed + stuck certificate renders, retrying
  // one, and opening a learner's certificate. Mirrors the endpoint group's own gate —
  // `/api/admin/certificates/*` is `Roles.Admin` alone, enforced per handler.
  //
  // Its own capability rather than `userAdministration`, which holds the same single role today: the two
  // mirror DIFFERENT backend sets, and if either is widened later (an operations role that is not a user
  // administrator is the obvious candidate) it must be possible to follow that set here without dragging
  // the user directory's authority along with it.
  certificateOperations: [Role.Admin],
  // Muutosjohtaminen, the consultant surface (ADR-0016 §4.5): the programme and everything filed under it
  // — the metric register, the quarterly values, workshops, raw observations, decisions, actions, the
  // tracking view and the report draft. Mirrors `Roles.ChangeProgramManagement`, which is the consultant
  // ALONE.
  //
  // The tenant ADMIN's absence is the part worth stating, because it is the only capability in this file
  // that a tenant administrator does not hold: §4.5 property 1 is that a change programme is the
  // consultant's case file and its raw observations are the module's strongest personal-data surface, and
  // chapter 8.3 names three roles of which the tenant administrator is not one. Adding Role.Admin here
  // would offer an administrator a surface the API answers 403 for — src/app/change-management-frame
  // .test.mjs reads this entry against `Roles.ChangeProgramManagement` and fails on exactly that drift.
  changeProgramManagement: [Role.ChangeLeadConsultant],
  // Muutosjohtaminen, the PUBLISHED half (ADR-0016 §4.5, §7.7): the one surface of this module a client
  // role may open — `GET /api/change-programs/change-map/published`, and in MJ-US-E6.2 the quarterly
  // report behind it. Mirrors `Roles.ChangePublishedReportRead`, which is the consultant AND the
  // customer's leadership reader.
  //
  // The CONSULTANT is on this row as well as on the one above, and that is §4.5's own arrangement rather
  // than a convenience: previewing what leadership sees is part of publishing it. The muutoskartta reads
  // it the other way round — a session holding `changeProgramManagement` opens the LIVE map and never this
  // route, because the two routes answer different questions and the live one is the consultant's.
  //
  // Its own capability and not a widening of `changeProgramManagement`, for the reason that capability's
  // own note gives in reverse: adding `MJ_JOHDON_RAPORTTI` there would light the whole consultant tab
  // strip for a reader the API answers 403 for on every tab of it.
  changePublishedReportRead: [Role.ChangeLeadConsultant, Role.ChangeLeadershipReportViewer],
  // Muutosjohtaminen, the ACTION OWNER's own surface (MJ-US-E7.2, ADR-0016 §4.5 property 3): the two
  // self-scoped routes under `/api/my/change-actions` — their own actions, and the state field on them.
  // Mirrors `Roles.ChangeOwnActionSurface`, which is the action owner ALONE.
  //
  // The CONSULTANT's absence is the part worth stating, and it is the mirror image of the tenant ADMIN's
  // absence from `changeProgramManagement` above. §4.4 keeps the consultant's door to an action's state on
  // their OWN register (`PUT …/actions/{actionId}`, MJ-US-E5.2), where the audit row names them; a
  // consultant on this surface would be a second way to write one column with a different actor in the
  // chain. Adding Role.ChangeLeadConsultant here would light a rail row the API answers 403 for —
  // src/changePrograms/own-action.test.mjs reads this entry against `Roles.ChangeOwnActionSurface` and
  // fails the build on exactly that drift.
  changeOwnActions: [Role.ChangeActionOwner],
  // HRM, the MASKED half (ADR-0020 §4, §16 task 5): the employment-record list and one person's record
  // with the henkilötunnus as `ddmmyyC****`, `hasSalary` instead of an amount and no work-permit data.
  // Mirrors `Roles.EmploymentRecordView`, which is the tenant ADMIN AND the HRM expert.
  //
  // The tenant ADMIN's PRESENCE is the part worth stating, because it is the opposite of
  // `changeProgramManagement`'s note above and the two are easy to read as one rule.
  // `employment-record.md`'s "Who may read what" table gives the administrator the masked henkilötunnus,
  // no amount at all, no work-permit data and the terms, placement and job title as before — and §16 task
  // 5's acceptance says so in as many words. What keeps that safe is not this row: it is that every shape
  // these routes can answer with has no property a guarded value could be written into
  // (`EmploymentRecordContractRules`).
  employmentRecordView: [Role.Admin, Role.HrmPersonnelExpert],
  // HRM, the GUARDED half (ADR-0020 §4, §5 point 5, §6 point 6): the two reveal buttons and the
  // work-permit section. Mirrors `Roles.HrmPersonnelRecord`, which is the HRM expert ALONE.
  //
  // The tenant ADMIN's absence is this capability's whole content. §5 point 5 splits two questions a
  // single role would run together: an administrator GRANTS this authority, because deciding who works
  // here is administration, and does not HOLD its data. Adding Role.Admin here would offer an
  // administrator a reveal the API answers 403 for — and `RoleModelGuardTests` fails the backend build if
  // the same edit is made to the set this row mirrors.
  hrmPersonnelRecord: [Role.HrmPersonnelExpert],
};

/** Whether the given roles include one that holds `capability`. */
export function hasCapability(roles: readonly string[], capability: Capability): boolean {
  const held = new Set(roles.map(normalize));
  return CAPABILITY_ROLES[capability].some((role) => held.has(role));
}

/**
 * Capability checks bound to the roles this session holds IN THE TENANT IT IS WORKING IN.
 *
 * ADR-0018 §4.2's rule, mirrored (task 2f6b370f): for the account's OWN tenant — which is every
 * single-tenant person, and the explicit "my own organisation" choice — the roles are the token's CLIENT
 * roles, exactly as they have always been. For a tenant reached BY MEMBERSHIP they are the role leaves of
 * that tenant's branch and the client roles do not travel, because that is the session the API will give
 * them: a consultant's `ADMIN` at home must not light this app's user-administration nav inside a
 * customer, and the `CONTENT_MANAGER` they were granted in that customer must.
 *
 * WHERE A MEMBERSHIP'S ROLES COME FROM, which ADR-0021 §9 changes. They used to be derived here from the
 * access token's `groups` claim (`auth/membershipRoles.ts`, now retired with it): §4.1 moved the carrier
 * into `tenant_memberships`, so the token carries nothing to derive and the app is TOLD them instead —
 * `GET /api/auth/memberships` answers the roles per membership (§5) and the selection carries them. The
 * rule itself is untouched: the account's own tenant (and the explicit "my own organisation" choice) keeps
 * the token's CLIENT roles exactly as always, and a tenant reached by membership gets that tenant's roles
 * and no client role travels with it.
 *
 * The SERVER applies §4.2's filter, which is the other half of §9: this file no longer holds a copy of
 * §3.3's code set to drift from the backend's, and a row this build should not honour never reaches the
 * answer. The API is still the only enforcement — a hand-typed URL is bounced by `RequireRole` — so being
 * wrong here grants nothing; it costs the person the door, or shows them one that answers 403.
 *
 * The choice comes from the SELECTION PROVIDER and not from `selectedTenant()`, so that switching
 * organisation re-renders the nav and the route guards with it. Through the non-throwing reader, because
 * the OPERATOR CONSOLE shares this hook and mounts no such provider: there it answers null, which is the
 * right answer — the operator is in Verso's own tenant (ADR-0014 A-2) and reaches no customer by
 * membership at all.
 */
export function useCapabilities(): {
  roles: readonly string[];
  can: (capability: Capability) => boolean;
} {
  const { session } = useSession();
  const choice = useTenantChoiceIfAny();
  const roles =
    choice?.kind === "membership" ? choice.membership.roles : (session?.roles ?? []);
  return { roles, can: (capability) => hasCapability(roles, capability) };
}
